import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Users,
  Phone,
  Globe,
  CurrencyDollar,
  TrendUp,
  Lightning,
  ArrowRight,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-white border border-border p-5 flex items-start gap-4 animate-fade-in-up"
    >
      <div
        className="w-10 h-10 flex items-center justify-center border"
        style={{ borderColor: accent, color: accent }}
      >
        <Icon weight="bold" size={20} />
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1">
          {label}
        </div>
        <div
          className="text-2xl font-bold tracking-tight"
          style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, leadsRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/leads?limit=5`),
        ]);
        setStats(statsRes.data);
        setRecentLeads(leadsRes.data.leads || []);
      } catch (e) {
        console.error("Dashboard load error:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const s = stats || {};

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1
            className="text-3xl sm:text-4xl font-black tracking-tighter"
            style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
          >
            Command Center
          </h1>
          <p className="text-sm text-[#52525B] mt-1">
            Pipeline overview and lead activity
          </p>
        </div>
        <button
          data-testid="quick-scan-btn"
          onClick={() => navigate("/scanner")}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#002FA7] text-white text-sm font-semibold border-none hover:bg-[#002482] transition-colors duration-100"
        >
          <Lightning weight="bold" size={16} />
          Quick Scan
        </button>
      </div>

      {/* Stat Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total Leads"
          value={s.total_leads || 0}
          accent="#002FA7"
          testId="stat-total-leads"
        />
        <StatCard
          icon={Phone}
          label="Called"
          value={s.called_leads || 0}
          accent="#52525B"
          testId="stat-called"
        />
        <StatCard
          icon={Globe}
          label="Websites Built"
          value={s.total_websites || 0}
          accent="#002FA7"
          testId="stat-websites"
        />
        <StatCard
          icon={CurrencyDollar}
          label="Revenue"
          value={`$${(s.revenue || 0).toLocaleString()}`}
          accent="#00C853"
          testId="stat-revenue"
        />
      </div>

      {/* Pipeline + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Pipeline */}
        <div className="lg:col-span-2 bg-white border border-border p-5">
          <div className="flex items-center justify-between mb-4">
            <h3
              className="text-lg font-bold tracking-tight"
              style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
            >
              Pipeline
            </h3>
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B]">
              {s.conversion_rate || 0}% close rate
            </span>
          </div>
          <div className="grid grid-cols-5 gap-2">
            {[
              { label: "New", count: s.new_leads, color: "#E5E5E5", textColor: "#0A0A0A" },
              { label: "Called", count: s.called_leads, color: "#DBEAFE", textColor: "#1E40AF" },
              { label: "Pitched", count: s.pitched_leads, color: "#FEF3C7", textColor: "#92400E" },
              { label: "Won", count: s.won_leads, color: "#D1FAE5", textColor: "#065F46" },
              { label: "Lost", count: s.lost_leads, color: "#FEE2E2", textColor: "#991B1B" },
            ].map((stage) => (
              <div
                key={stage.label}
                className="text-center p-3 border border-border"
                style={{ background: stage.color }}
              >
                <div
                  className="text-2xl font-bold"
                  style={{ color: stage.textColor, fontFamily: "Cabinet Grotesk, sans-serif" }}
                >
                  {stage.count || 0}
                </div>
                <div
                  className="text-xs font-semibold mt-1"
                  style={{ color: stage.textColor }}
                >
                  {stage.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white border border-border p-5">
          <h3
            className="text-lg font-bold tracking-tight mb-4"
            style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
          >
            Quick Actions
          </h3>
          <div className="space-y-2">
            {[
              { label: "Scan for Leads", to: "/scanner", icon: "search" },
              { label: "Build a Website", to: "/generator", icon: "lightning" },
              { label: "Manage Leads", to: "/leads", icon: "users" },
              { label: "Launch a Site", to: "/launch", icon: "rocket" },
            ].map((action) => (
              <button
                key={action.label}
                data-testid={`quick-${action.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => navigate(action.to)}
                className="w-full flex items-center justify-between px-4 py-3 border border-border text-sm font-medium text-[#0A0A0A] hover:border-[#002FA7] hover:text-[#002FA7] transition-all duration-100"
              >
                {action.label}
                <ArrowRight size={14} />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Leads */}
      {recentLeads.length > 0 && (
        <div className="bg-white border border-border">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h3
              className="text-lg font-bold tracking-tight"
              style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
            >
              Recent Leads
            </h3>
            <button
              data-testid="view-all-leads-btn"
              onClick={() => navigate("/leads")}
              className="text-xs font-bold text-[#002FA7] hover:underline"
            >
              View All
            </button>
          </div>
          <table className="w-full dense-table">
            <thead>
              <tr>
                <th className="text-left">Business</th>
                <th className="text-left">Phone</th>
                <th className="text-left">Score</th>
                <th className="text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {recentLeads.map((lead) => (
                <tr
                  key={lead.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/leads`)}
                >
                  <td className="font-medium">{lead.business_name || "—"}</td>
                  <td className="font-mono text-xs">
                    {lead.phones?.[0] || "—"}
                  </td>
                  <td>
                    <span
                      className={`inline-block px-2 py-0.5 text-xs font-bold grade-${(lead.quality_grade || "f").toLowerCase()}`}
                    >
                      {lead.quality_grade || "?"}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`inline-block px-2 py-0.5 text-xs font-semibold status-${lead.status || "new"}`}
                    >
                      {lead.status || "new"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
